/**
 * PendingAttachmentsPicker — Selector de adjuntos "en memoria" para usar al
 * CREAR un pedido/factura, cuando todavía no existe el id padre para subir.
 *
 * Bufferea File[] (value/onChange controlado por el form). El form se encarga
 * de subirlos vía uploadOrderAttachmentFile/uploadDocumentAttachmentFile una
 * vez que la creación devuelve el id. Valida tipos y tamaño total (≤ 20 MB),
 * igual que el backend.
 */
import { useState } from 'react'
import { Upload, Paperclip, Trash2 } from 'lucide-react'

const MAX_TOTAL_BYTES = 20 * 1024 * 1024
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
const ALLOWED_EXT_RE = /\.(pdf|jpe?g|png|webp)$/i

export function PendingAttachmentsPicker({
  files,
  onChange,
}: {
  files: File[]
  onChange: (files: File[]) => void
}) {
  const [error, setError] = useState<string | null>(null)

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return
    setError(null)
    const incoming = Array.from(list)
    for (const f of incoming) {
      const okType = ALLOWED_MIME.includes(f.type) || ALLOWED_EXT_RE.test(f.name)
      if (!okType) {
        setError(`Tipo no permitido: ${f.name}. Solo PDF, JPEG, PNG, WEBP.`)
        return
      }
    }
    const next = [...files, ...incoming]
    const total = next.reduce((s, f) => s + f.size, 0)
    if (total > MAX_TOTAL_BYTES) {
      setError('El tamaño total supera los 20 MB.')
      return
    }
    onChange(next)
  }

  const removeAt = (idx: number) => {
    setError(null)
    onChange(files.filter((_, i) => i !== idx))
  }

  return (
    <div>
      <p className="text-xs text-content-muted mb-3">
        Máx. 20 MB total. Tipos permitidos: PDF, JPEG, PNG, WEBP. Se subirán al guardar.
      </p>

      <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-dashed border-divider-strong hover:border-primary hover:bg-primary-50 transition-colors text-xs text-content-muted hover:text-primary">
        <Upload size={14} />
        Agregar archivo
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ''
          }}
          className="hidden"
        />
      </label>

      {error && <p className="mt-2 text-xs text-danger-solid">{error}</p>}

      {files.length > 0 ? (
        <div className="mt-4 space-y-2">
          {files.map((f, idx) => (
            <div
              key={`${f.name}-${idx}`}
              className="flex items-center gap-3 p-2.5 bg-glass-3 rounded-lg border border-divider group"
            >
              <Paperclip size={14} className="text-content-subtle flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-content truncate">{f.name}</p>
                <p className="text-[10px] text-content-subtle">{(f.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="p-1.5 text-content-subtle hover:text-danger-solid transition-colors opacity-0 group-hover:opacity-100"
                title="Quitar"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs text-content-subtle italic">Sin archivos seleccionados.</p>
      )}
    </div>
  )
}
